import dayjs from 'dayjs';
import { Asset } from 'types/Attachments';

const ASSET_FORMAT_NAMES: Record<string, string> = {
  jpeg: 'JPG',
  jpg: 'JPG',
  pjpeg: 'JPG',
  quicktime: 'MOV',
  'x-msvideo': 'AVI',
};

export function getAssetFormatName(mimeType: string): string {
  const subtype = mimeType.split('/')[1] ?? mimeType;
  return ASSET_FORMAT_NAMES[subtype] ?? subtype.toUpperCase();
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function formatDimensions(asset: Asset): string {
  if (asset.width && asset.height) return `${asset.width}×${asset.height}`;
  return '—';
}

const SIMPLE_RATIO_MAX_TERM = 18;

function nearestSimpleRatio(longToShort: number): [number, number] {
  const maxLong = Math.max(SIMPLE_RATIO_MAX_TERM, Math.round(longToShort));
  let nearest: [number, number] = [Math.round(longToShort), 1];
  for (let short = 2; short <= SIMPLE_RATIO_MAX_TERM; short++) {
    const long = Math.round(short * longToShort);
    if (
      long <= maxLong &&
      Math.abs(long / short - longToShort) <
        Math.abs(nearest[0] / nearest[1] - longToShort)
    ) {
      nearest = [long, short];
    }
  }
  return nearest;
}

export function formatAspectRatio(asset: Asset): string {
  const { width, height } = asset;
  if (!width || !height) return '—';
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  const exactLong = Math.max(width, height) / divisor;
  const exactShort = Math.min(width, height) / divisor;
  if (exactLong <= SIMPLE_RATIO_MAX_TERM || exactShort === 1) {
    return `${width / divisor}:${height / divisor}`;
  }
  const [long, short] = nearestSimpleRatio(exactLong / exactShort);
  return width > height ? `~${long}:${short}` : `~${short}:${long}`;
}

export function formatLibraryDate(date: string): string {
  return dayjs(date).format('D.MM.YYYY');
}

export function formatCreatedAt(asset: Asset): string {
  return formatLibraryDate(asset.created_at);
}
